import React, { useMemo } from 'react';
import { X, Server } from 'lucide-react';
import { LocationData } from '../types';
import { SERVER_TYPES } from '../constants';

interface FleetOverviewModalProps {
  onClose: () => void;
  locations: Record<string, LocationData>;
  formatCurrency: (amount: number, decimals?: number) => string;
}

const TIER_COLORS: Record<number, string> = {
  0: 'bg-gray-600 text-gray-200',
  1: 'bg-green-700 text-green-100',
  2: 'bg-blue-700 text-blue-100',
  3: 'bg-indigo-700 text-indigo-100',
  4: 'bg-purple-700 text-purple-100',
  5: 'bg-pink-700 text-pink-100',
  6: 'bg-red-700 text-red-100',
};

const FleetOverviewModal: React.FC<FleetOverviewModalProps> = ({ onClose, locations, formatCurrency }) => {
  const { byType, byLocation, totals } = useMemo(() => {
    const typeMap: Record<string, { count: number; online: number; contracted: number; satisfactionSum: number; satisfactionCount: number; ageStages: string[] }> = {};
    const locationRows: { name: string; count: number; online: number; powerKW: number; satisfactionSum: number; satisfactionCount: number; revenue: number }[] = [];

    let totalServers = 0;
    let totalOnline = 0;
    let totalContracted = 0;
    let totalFailed = 0;
    let totalRevenuePotential = 0;

    Object.entries(locations).forEach(([, loc]) => {
      if (!loc.unlocked) return;
      let locCount = 0;
      let locOnline = 0;
      let locPower = 0;
      let locSatSum = 0;
      let locSatCount = 0;
      let locRevenue = 0;

      Object.values(loc.floors ?? {}).forEach(floor => {
        floor.racks?.forEach(rack => {
          rack.servers?.forEach(server => {
            const st = SERVER_TYPES.find(t => t.id === server.type);
            if (!st) return;

            totalServers++;
            locCount++;
            locPower += st.powerDraw;

            if (server.failureStatus !== 'failed') {
              totalOnline++;
              locOnline++;
            } else {
              totalFailed++;
            }
            if (server.contractId) {
              totalContracted++;
              locRevenue += st.revenue;
            }
            totalRevenuePotential += st.revenue;

            const sat = server.satisfactionScore ?? 100;
            locSatSum += sat;
            locSatCount++;

            if (!typeMap[server.type]) {
              typeMap[server.type] = { count: 0, online: 0, contracted: 0, satisfactionSum: 0, satisfactionCount: 0, ageStages: [] };
            }
            const tm = typeMap[server.type];
            tm.count++;
            if (server.failureStatus !== 'failed') tm.online++;
            if (server.contractId) tm.contracted++;
            tm.satisfactionSum += sat;
            tm.satisfactionCount++;
            if (server.ageStage) tm.ageStages.push(server.ageStage);
          });
        });
      });

      if (locCount > 0) {
        locationRows.push({
          name: loc.name,
          count: locCount,
          online: locOnline,
          powerKW: locPower,
          satisfactionSum: locSatSum,
          satisfactionCount: locSatCount,
          revenue: locRevenue,
        });
      }
    });

    const byType = Object.entries(typeMap)
      .map(([typeId, data]) => {
        const st = SERVER_TYPES.find(t => t.id === typeId)!;
        const stageCounts: Record<string, number> = {};
        data.ageStages.forEach(s => { stageCounts[s] = (stageCounts[s] ?? 0) + 1; });
        const dominantStage = Object.entries(stageCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'new';
        return {
          st,
          count: data.count,
          online: data.online,
          contracted: data.contracted,
          avgSat: data.satisfactionCount > 0 ? Math.round(data.satisfactionSum / data.satisfactionCount) : 100,
          dominantStage,
          revenuePotential: st.revenue * data.count,
        };
      })
      .sort((a, b) => b.st.unlockLevel - a.st.unlockLevel || b.count - a.count);

    return {
      byType,
      byLocation: locationRows.sort((a, b) => b.count - a.count),
      totals: { totalServers, totalOnline, totalContracted, totalFailed, totalRevenuePotential },
    };
  }, [locations]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <Server size={20} className="text-blue-400" />
            <h2 className="text-lg font-bold text-white">Fleet Overview</h2>
            <span className="text-sm text-gray-400">{totals.totalServers} servers across all locations</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {/* Summary row */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: 'Total Servers', value: totals.totalServers, color: 'text-white' },
              { label: 'Online', value: totals.totalOnline, color: 'text-emerald-400' },
              { label: 'Under Contract', value: totals.totalContracted, color: 'text-teal-400' },
              { label: 'Failed / Repairing', value: totals.totalFailed, color: totals.totalFailed > 0 ? 'text-red-400' : 'text-gray-500' },
              { label: 'Rev Potential/Day', value: formatCurrency(totals.totalRevenuePotential), color: 'text-yellow-400' },
            ].map(s => (
              <div key={s.label} className="bg-gray-800 rounded-lg p-3 text-center">
                <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* By server type */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">By Server Type</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-700">
                    <th className="pb-2 pr-3">Model</th>
                    <th className="pb-2 pr-3 text-center">Tier</th>
                    <th className="pb-2 pr-3 text-right">Count</th>
                    <th className="pb-2 pr-3 text-right">Online</th>
                    <th className="pb-2 pr-3 text-right">Contracted</th>
                    <th className="pb-2 pr-3 text-right">Avg Sat</th>
                    <th className="pb-2 pr-3 text-center">Age</th>
                    <th className="pb-2 text-right">Rev Potential/Day</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {byType.map(row => (
                    <tr key={row.st.id} className="hover:bg-gray-800/50">
                      <td className="py-2 pr-3 font-medium text-white">{row.st.name}</td>
                      <td className="py-2 pr-3 text-center">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${TIER_COLORS[row.st.unlockLevel] ?? TIER_COLORS[0]}`}>
                          L{row.st.unlockLevel}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right text-gray-300">{row.count}</td>
                      <td className="py-2 pr-3 text-right text-emerald-400">{row.online}</td>
                      <td className="py-2 pr-3 text-right text-teal-400">{row.contracted}</td>
                      <td className={`py-2 pr-3 text-right ${row.avgSat >= 80 ? 'text-emerald-400' : row.avgSat >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {row.avgSat}%
                      </td>
                      <td className="py-2 pr-3 text-center text-xs capitalize text-gray-400">{row.dominantStage}</td>
                      <td className="py-2 text-right text-yellow-400">{formatCurrency(row.revenuePotential)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {byType.length === 0 && (
                <p className="text-center text-gray-500 py-8">No servers installed yet.</p>
              )}
            </div>
          </div>

          {/* By location */}
          {byLocation.length > 1 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">By Location</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-700">
                      <th className="pb-2 pr-3">Location</th>
                      <th className="pb-2 pr-3 text-right">Servers</th>
                      <th className="pb-2 pr-3 text-right">Online</th>
                      <th className="pb-2 pr-3 text-right">Contract Rev/Day</th>
                      <th className="pb-2 pr-3 text-right">Power Draw</th>
                      <th className="pb-2 text-right">Avg Sat</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {byLocation.map(row => {
                      const avgSat = row.satisfactionCount > 0 ? Math.round(row.satisfactionSum / row.satisfactionCount) : 100;
                      return (
                        <tr key={row.name} className="hover:bg-gray-800/50">
                          <td className="py-2 pr-3 font-medium text-white">{row.name}</td>
                          <td className="py-2 pr-3 text-right text-gray-300">{row.count}</td>
                          <td className="py-2 pr-3 text-right text-emerald-400">{row.online}</td>
                          <td className="py-2 pr-3 text-right text-teal-400">{formatCurrency(row.revenue)}</td>
                          <td className="py-2 pr-3 text-right text-yellow-400">{row.powerKW.toFixed(1)} kW</td>
                          <td className={`py-2 text-right ${avgSat >= 80 ? 'text-emerald-400' : avgSat >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {avgSat}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FleetOverviewModal;
